import { BlockStatus, DraftSource } from '@wiztime/core';
import { useTranslation } from 'react-i18next';
import { LayoutMode, useLayoutMode } from '../hooks/useMediaQuery';
import { useRunCommand } from '../hooks/useRunCommand';
import { useStore } from '../hooks/useServices';
import { useUndoVersionReader } from '../hooks/useUndoVersion';
import { useVisibleBlocks } from '../hooks/useVisibleBlocks';
import { Button } from '../ui/Button';
import styles from './DraftBanner.module.css';
import { reportConfirm } from './confirmToast';
import { toastUndo } from './toastStore';

/**
 * Shown when the viewed date has visible drafts: count, sources and the two bulk actions. On a
 * phone it is one 52px row (the sources move into its tooltip) and scrolls with the page.
 */
export function DraftBanner({ date }: { date: string }) {
  const { t } = useTranslation();
  const store = useStore();
  const run = useRunCommand();
  const undoVersion = useUndoVersionReader();
  const narrow = useLayoutMode() === LayoutMode.Narrow;
  const { blocks } = useVisibleBlocks(date);
  const drafts = blocks.filter((block) => block.data.status === BlockStatus.Draft);
  if (drafts.length === 0) return null;

  const sources = [
    drafts.some((block) => block.data.draftSource === DraftSource.Template)
      ? t('draft.sourceTemplate')
      : null,
    drafts.some((block) => block.data.draftSource === DraftSource.Repeat) ? t('draft.sourceRepeat') : null,
  ].filter((source): source is string => source !== null);

  const sourceText = t('draft.sourcePrefix', { sources: sources.join(t('draft.sourceJoin')) });
  const title = t('draft.bannerTitle', { count: drafts.length });
  // Both bulk actions report back with a toast that offers undo (one undo step each).
  const withUndoToast = async (
    action: () => Promise<{ ok: true; value: string[] } | { ok: false }>,
    message: (count: number) => string,
  ): Promise<void> => {
    const result = await action();
    if (!result.ok) return;
    toastUndo(message(result.value.length), t('toast.undo'), undoVersion(), () => {
      void run(() => store.undo());
    });
  };
  const confirmAll = (): void => {
    void (async () => {
      const result = await run(() => store.confirmAllDrafts(date));
      if (!result.ok) return;
      // The project of the one removed draft names the record it duplicates (§10.1).
      const removed = drafts.find((block) => block.id === result.value.removedAsDuplicate[0]);
      await reportConfirm(store, t, result.value, removed?.data.projectId ?? '', undoVersion(), () => {
        void run(() => store.undo());
      });
    })();
  };
  const clearAll = (): void => {
    void withUndoToast(
      () => run(() => store.clearDrafts(date)),
      (count) => t('toast.clearedDrafts', { count }),
    );
  };

  return (
    <section
      className={styles.banner}
      aria-label={title}
      title={narrow ? `${title}. ${sourceText}` : undefined}
      data-testid="draft-banner"
    >
      <div className={styles.text}>
        <p className={styles.title}>{narrow ? t('draft.bannerShort', { count: drafts.length }) : title}</p>
        {narrow ? null : <p className={styles.source}>{sourceText}</p>}
      </div>
      <div className={styles.actions}>
        <Button variant="danger" onClick={clearAll}>
          {t('draft.clear')}
        </Button>
        <Button variant="primary" onClick={confirmAll}>
          {t('draft.confirmAll')}
        </Button>
      </div>
    </section>
  );
}
